/**
 * CONSAI Business App - Admin Vouchers Management
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Tag, Edit, Trash2, Eye, Loader2,
  Calendar, Users, Percent, IndianRupee
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVouchers = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [usageData, setUsageData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    applicable_on: ['all'],
    min_purchase_amount: '',
    valid_from: '',
    valid_until: '',
    usage_limit: ''
  });

  useEffect(() => {
    fetchVouchers();
  }, [filterStatus]);

  const fetchVouchers = async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await fetch(`${API_URL}/api/admin/vouchers/${params}`, {
        credentials: 'include'
      });
      
      if (res.status === 401) {
        navigate('/admin');
        return;
      }
      
      const data = await res.json();
      setVouchers(data);
    } catch (error) {
      toast.error('Error loading vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.code || !formData.discount_value || !formData.valid_from || !formData.valid_until) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          min_purchase_amount: formData.min_purchase_amount ? parseFloat(formData.min_purchase_amount) : null,
          usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null
        })
      });
      
      if (res.ok) {
        toast.success('Voucher created');
        setShowCreateDialog(false);
        resetForm();
        fetchVouchers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error creating voucher');
      }
    } catch (error) {
      toast.error('Error creating voucher');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/${selectedVoucher.voucher_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          discount_value: parseFloat(formData.discount_value),
          status: formData.status,
          valid_until: formData.valid_until,
          usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null
        })
      });
      
      if (res.ok) {
        toast.success('Voucher updated');
        setShowEditDialog(false);
        fetchVouchers();
      } else {
        toast.error('Error updating voucher');
      }
    } catch (error) {
      toast.error('Error updating voucher');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (voucher) => {
    if (!confirm('Delete this voucher?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/${voucher.voucher_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Voucher deleted');
        fetchVouchers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error deleting voucher');
      }
    } catch (error) {
      toast.error('Error deleting voucher');
    }
  };

  const fetchUsage = async (voucher) => {
    setSelectedVoucher(voucher);
    setShowUsageDialog(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/${voucher.voucher_id}/usage`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setUsageData(data.redemptions);
      }
    } catch (error) {
      toast.error('Error loading usage');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      applicable_on: ['all'],
      min_purchase_amount: '',
      valid_from: '',
      valid_until: '',
      usage_limit: ''
    });
  };

  const openEditDialog = (voucher) => {
    setSelectedVoucher(voucher);
    setFormData({
      ...formData,
      discount_value: voucher.discount_value.toString(),
      valid_until: voucher.valid_until?.split('T')[0] || '',
      usage_limit: voucher.usage_limit?.toString() || '',
      status: voucher.status
    });
    setShowEditDialog(true);
  };

  const getStatusBadge = (voucher) => {
    const now = new Date();
    const validUntil = new Date(voucher.valid_until);
    
    if (voucher.status === 'inactive') {
      return <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>;
    }
    if (validUntil < now) {
      return <Badge className="bg-red-100 text-red-600">Expired</Badge>;
    }
    if (voucher.usage_limit && voucher.times_used >= voucher.usage_limit) {
      return <Badge className="bg-orange-100 text-orange-600">Limit Reached</Badge>;
    }
    return <Badge className="bg-green-100 text-green-600">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#021945]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold font-manrope text-slate-900">
              Voucher Management
            </h1>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#021945] hover:bg-[#021945]/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Voucher
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Filter */}
        <div className="mb-6 flex gap-4">
          <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Vouchers Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vouchers.map((voucher) => (
            <Card key={voucher.voucher_id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-manrope flex items-center gap-2">
                      <Tag className="h-5 w-5 text-[#d7263d]" />
                      {voucher.code}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {voucher.discount_type === 'percentage' ? (
                        <span className="flex items-center gap-1">
                          <Percent className="h-4 w-4" />
                          {voucher.discount_value}% off
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <IndianRupee className="h-4 w-4" />
                          ₹{voucher.discount_value} off
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  {getStatusBadge(voucher)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Valid: {new Date(voucher.valid_from).toLocaleDateString()} - {new Date(voucher.valid_until).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Used: {voucher.times_used} {voucher.usage_limit ? `/ ${voucher.usage_limit}` : '(unlimited)'}
                  </div>
                  <div>
                    Applicable: {voucher.applicable_on.join(', ')}
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => fetchUsage(voucher)}>
                    <Eye className="h-4 w-4 mr-1" />
                    Usage
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(voucher)}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {voucher.times_used === 0 && (
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(voucher)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {vouchers.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No vouchers found</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope">Create Voucher</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g. SAVE20"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value *</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valid From *</Label>
                <Input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Valid Until *</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Purchase (₹)</Label>
                <Input
                  type="number"
                  value={formData.min_purchase_amount}
                  onChange={(e) => setFormData({ ...formData, min_purchase_amount: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label>Usage Limit</Label>
                <Input
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-[#021945]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope">Edit Voucher: {selectedVoucher?.code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Discount Value</Label>
              <Input
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Valid Until</Label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={submitting} className="bg-[#021945]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={showUsageDialog} onOpenChange={setShowUsageDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-manrope">Voucher Usage: {selectedVoucher?.code}</DialogTitle>
            <DialogDescription>Total redemptions: {usageData.length}</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {usageData.length > 0 ? (
              <div className="space-y-2">
                {usageData.map((usage, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="font-medium">{usage.user_name || usage.user_email}</p>
                    <p className="text-slate-500">
                      ₹{usage.discount_amount / 100} saved • {new Date(usage.redeemed_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No redemptions yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVouchers;
